import { defaultManualModules } from "../data/manualModules";
import type {
  CommunicationModule,
  LanguageManualModule,
  LanguagePatternFormula,
  ManualModule,
  ManualModuleSnapshot,
  ManualRevision,
  PracticeManualModule,
  PracticeTechnique
} from "../types";
import { createId, nowIso } from "../utils/ids";

const MANUAL_MODULES_KEY = "psychic-master.manual-modules.v2";
const LEGACY_MODULES_KEY = "psychic-master.modules.v1";

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toSnapshot(module: ManualModule): ManualModuleSnapshot {
  const { revisions: _revisions, ...snapshot } = module;
  return clone(snapshot);
}

function formulaKey(formula: LanguagePatternFormula): string {
  return (formula.id || formula.name + formula.manualFormula).toLowerCase();
}

function practiceKey(practice: PracticeTechnique): string {
  return (practice.id || practice.name + practice.intent).toLowerCase();
}

function mergeFormulas(defaults: LanguagePatternFormula[], current: LanguagePatternFormula[]): LanguagePatternFormula[] {
  const byKey = new Map<string, LanguagePatternFormula>();
  defaults.forEach((formula) => byKey.set(formulaKey(formula), clone(formula)));
  current.forEach((formula) => byKey.set(formulaKey(formula), clone(formula)));
  return Array.from(byKey.values());
}

function mergePractices(defaults: PracticeTechnique[], current: PracticeTechnique[]): PracticeTechnique[] {
  const byKey = new Map<string, PracticeTechnique>();
  defaults.forEach((practice) => byKey.set(practiceKey(practice), clone(practice)));
  current.forEach((practice) => byKey.set(practiceKey(practice), clone(practice)));
  return Array.from(byKey.values());
}

function normalizeModules(modules: ManualModule[]): ManualModule[] {
  return modules.map((module) => ({
    ...module,
    active: module.active !== false,
    tags: module.tags ?? [],
    keywords: module.keywords ?? [],
    revisions: module.revisions ?? [],
    updatedAt: module.updatedAt || nowIso()
  })) as ManualModule[];
}

function mergeDefaultManualModules(modules: ManualModule[]): ManualModule[] {
  const currentById = new Map(modules.map((module) => [module.id, clone(module)]));

  for (const defaultModule of defaultManualModules) {
    const current = currentById.get(defaultModule.id);
    if (!current) {
      currentById.set(defaultModule.id, clone(defaultModule));
      continue;
    }

    if (defaultModule.domain === "language-pattern" && current.domain === "language-pattern") {
      const merged: LanguageManualModule = {
        ...defaultModule,
        ...current,
        formulas: mergeFormulas(defaultModule.formulas, current.formulas),
        visualCue: current.visualCue || defaultModule.visualCue
      };
      currentById.set(defaultModule.id, merged);
    }

    if (defaultModule.domain === "psi-qigong" && current.domain === "psi-qigong") {
      const merged: PracticeManualModule = {
        ...defaultModule,
        ...current,
        practices: mergePractices(defaultModule.practices, current.practices),
        visualCue: current.visualCue || defaultModule.visualCue
      };
      currentById.set(defaultModule.id, merged);
    }
  }

  return normalizeModules(Array.from(currentById.values())).sort((a, b) => a.sortOrder - b.sortOrder);
}

function convertLegacyModules(): ManualModule[] {
  const stored = localStorage.getItem(LEGACY_MODULES_KEY);
  if (!stored) return [];

  try {
    const legacyModules = JSON.parse(stored) as CommunicationModule[];
    if (!Array.isArray(legacyModules)) return [];

    return legacyModules.map((legacy, index) => {
      const createdAt = legacy.createdAt || nowIso();
      if (legacy.domain === "solo-practice") {
        const module: PracticeManualModule = {
          id: "legacy_practice_" + legacy.id,
          domain: "psi-qigong",
          title: legacy.title,
          description: legacy.description,
          category: legacy.category,
          sourceLabel: "이전 모듈 변환",
          tags: legacy.tags ?? [],
          keywords: legacy.tags ?? [],
          active: legacy.active,
          sortOrder: 500 + index,
          createdAt,
          updatedAt: legacy.updatedAt || createdAt,
          visualCue: legacy.cards[0]?.visualCue,
          practices: legacy.cards.map((card, cardIndex) => ({
            id: "legacy_practice_" + card.id,
            group: card.formulaGroup || legacy.category,
            name: card.title || "이전 훈련 " + (cardIndex + 1),
            intent: card.principle || legacy.intent,
            recommendedFor: card.triggerSituations ?? [],
            keywords: card.tags ?? [],
            setup: card.setup ?? [],
            steps: card.steps ?? [],
            duration: "5-10분",
            difficulty: "입문",
            safety: card.warnings ?? [],
            customizationPrompts: card.prompts ?? [],
            successCriteria: card.successCriteria ?? [],
            cautions: card.warnings ?? [],
            tags: card.tags ?? [],
            visualCue: card.visualCue
          })),
          revisions: []
        };
        return module;
      }

      const module: LanguageManualModule = {
        id: "legacy_language_" + legacy.id,
        domain: "language-pattern",
        title: legacy.title,
        description: legacy.description,
        category: legacy.category,
        sourceLabel: "이전 모듈 변환",
        tags: legacy.tags ?? [],
        keywords: legacy.tags ?? [],
        active: legacy.active,
        sortOrder: 400 + index,
        createdAt,
        updatedAt: legacy.updatedAt || createdAt,
        visualCue: legacy.cards[0]?.visualCue,
        formulas: legacy.cards.map((card, cardIndex) => ({
          id: "legacy_formula_" + card.id,
          group: card.formulaGroup || legacy.category,
          name: card.title || "이전 공식 " + (cardIndex + 1),
          manualFormula: card.manualFormula || card.formula || card.principle,
          practiceFormula: card.practiceFormula || card.sentenceTemplates[0] || card.principle,
          principle: card.principle,
          triggerSituations: card.triggerSituations ?? [],
          examples: card.exampleDialogues ?? [],
          cautions: card.warnings ?? [],
          tags: card.tags ?? [],
          visualCue: card.visualCue
        })),
        revisions: []
      };
      return module;
    });
  } catch {
    return [];
  }
}

export function loadManualModules(): ManualModule[] {
  const stored = localStorage.getItem(MANUAL_MODULES_KEY);
  if (!stored) {
    return mergeDefaultManualModules([...defaultManualModules, ...convertLegacyModules()]);
  }

  try {
    const parsed = JSON.parse(stored) as ManualModule[];
    return mergeDefaultManualModules(Array.isArray(parsed) ? parsed : defaultManualModules);
  } catch {
    return mergeDefaultManualModules(defaultManualModules);
  }
}

export function saveManualModules(modules: ManualModule[]): void {
  localStorage.setItem(MANUAL_MODULES_KEY, JSON.stringify(modules));
}

export function createManualRevision(module: ManualModule, note = "수정 전 자동 저장"): ManualRevision {
  return {
    id: createId("revision"),
    savedAt: nowIso(),
    note,
    snapshot: toSnapshot(module)
  };
}

export function restoreManualSnapshot(current: ManualModule, snapshot: ManualModuleSnapshot): ManualModule {
  const restored = {
    ...current,
    ...clone(snapshot),
    updatedAt: nowIso(),
    revisions: [createManualRevision(current, "복원 전 자동 저장"), ...current.revisions].slice(0, 20)
  } as ManualModule;
  return restored;
}

export function duplicateManualModule(module: ManualModule): ManualModule {
  const created = nowIso();
  if (module.domain === "language-pattern") {
    return {
      ...clone(module),
      id: createId("module"),
      title: module.title + " 복사본",
      sortOrder: Date.now(),
      createdAt: created,
      updatedAt: created,
      revisions: [],
      formulas: module.formulas.map((formula) => ({ ...formula, id: createId("formula") }))
    };
  }

  return {
    ...clone(module),
    id: createId("module"),
    title: module.title + " 복사본",
    sortOrder: Date.now(),
    createdAt: created,
    updatedAt: created,
    revisions: [],
    practices: module.practices.map((practice) => ({ ...practice, id: createId("practice") }))
  };
}

export function createBlankManualModule(domain: "language-pattern" | "psi-qigong"): ManualModule {
  const created = nowIso();
  if (domain === "language-pattern") {
    return {
      id: createId("module"),
      domain,
      title: "새 언어 패턴 매뉴얼",
      description: "주제, 키워드, 상황에 맞춰 문장으로 전환할 공식 모듈입니다.",
      category: "언어 패턴",
      sourceLabel: "사용자 추가",
      tags: ["언어 패턴"],
      keywords: [],
      active: true,
      sortOrder: Date.now(),
      createdAt: created,
      updatedAt: created,
      formulas: [
        {
          id: createId("formula"),
          group: "기본 묶음",
          name: "새 공식",
          manualFormula: "매뉴얼의 원문 공식을 입력하세요.",
          practiceFormula: "실전에서 바로 쓸 수 있는 문장 공식으로 바꾸세요.",
          principle: "공식의 핵심 원리를 입력하세요.",
          triggerSituations: [],
          examples: [],
          cautions: ["강압, 기만, 공포 유발로 사용하지 않습니다."],
          tags: []
        }
      ],
      revisions: []
    };
  }

  return {
    id: createId("module"),
    domain,
    title: "새 초능력/기공 매뉴얼",
    description: "주제, 키워드, 상황에 맞춰 추천하고 맞춤 실행 매뉴얼을 생성할 훈련 모듈입니다.",
    category: "초능력/기공",
    sourceLabel: "사용자 추가",
    tags: ["훈련"],
    keywords: [],
    active: true,
    sortOrder: Date.now(),
    createdAt: created,
    updatedAt: created,
    practices: [
      {
        id: createId("practice"),
        group: "기본 묶음",
        name: "새 훈련",
        intent: "훈련 목적을 입력하세요.",
        recommendedFor: [],
        keywords: [],
        setup: ["장소와 자세를 정합니다."],
        steps: ["1단계를 입력하세요."],
        duration: "5-10분",
        difficulty: "입문",
        safety: ["불편감이 생기면 즉시 멈춥니다."],
        customizationPrompts: [],
        successCriteria: [],
        cautions: ["의학적 치료나 검증된 초능력 주장으로 사용하지 않습니다."],
        tags: []
      }
    ],
    revisions: []
  };
}
